import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Autocomplete,
  Checkbox,
  Chip,
  ListSubheader,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { stopWheelNudge } from '../utils/numberInput';
import { isStudioScopedRole } from '../utils/roles';
import { useMyLocations } from '../hooks/useMyLocations';
import { studioIdOf } from '../utils/studioOf';
import BranchSelect from './BranchSelect';
import { LocationChecks, useCreateAtLocations } from './multiLocation/shared';

interface CouponFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  coupon?: any;
}

const CouponFormDialog: React.FC<CouponFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  coupon,
}) => {
  const isEditMode = !!coupon;
  const { admin } = useAuth();
  const isStudioRole = isStudioScopedRole(admin?.role);
  // Under "All locations" a new coupon has no implied home — the switcher
  // sends __all__, which the server refuses for writes — so ask. An edit is
  // pinned to the location that owns the coupon.
  const { isAllLocations, selectedLocationId } = useMyLocations();
  const [branchForNew, setBranchForNew] = useState('');
  useEffect(() => {
    if (open) setBranchForNew('');
  }, [open]);
  // Class-type coupons are a Match Day–only feature for now. Gated by the
  // studio's whitelabel app source (stable across environments), not its _id.
  const isMatchdayStudio =
    (admin?.studio as any)?.whitelabelConfig?.appSource === 'matchday';

    const [formData, setFormData] = useState({
      code: '',
      title: '',
      description: '',
      discountType: 'amount' as 'amount' | 'percentage',
      discountValue: '',
      maxDiscountAmount: '',
      applicableType: 'all' as 'all' | 'studio' | 'event' | 'class' | 'class_type' | 'all_events' | 'all_classes' | 'all_packs' | 'pack' | 'studio_packs' | 'global_packs' | 'all_merchandise' | 'merchandise',
      merchandise: '',
      studio: '',
      event: '',
      class: '',
      classType: '',
      // Multi-select: one coupon can cover any number of packs. The backend
      // still accepts the legacy single `pack`, but we always send `packs`.
      packs: [] as string[],
      minOrderAmount: '',
      maxUsage: '',
      isActive: true,
      showOnCheckout: false,
      validFrom: '',
      validUntil: '',
      expiresAt: '',
    });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [studios, setStudios] = useState<any[]>([]);

  // ── CREATE AT SEVERAL LOCATIONS ──────────────────────────────────────────
  // A brand with 2+ locations this account may manage ticks where the coupon
  // WORKS: it is still ONE coupon with one code, belonging to the first
  // ticked location, and also accepted at the others (`applicableStudios`).
  // An event, class, pack or product belongs to one location, so those
  // coupons are for one location only. Studios without such a brand keep the
  // form exactly as it was (`atLocs.multi` false → nothing shown or sent).
  const superStudioCoupon =
    !isStudioRole &&
    (formData.applicableType === 'studio' || formData.applicableType === 'studio_packs') &&
    !!formData.studio;
  // Editing: the coupon's own location is fixed; its extra locations change.
  const editOwner = isEditMode
    ? studioIdOf(coupon?.createdByStudio) || (superStudioCoupon ? formData.studio : '')
    : '';
  const originalExtras: string[] = (coupon?.applicableStudios ?? []).map((x: any) => studioIdOf(x)).filter(Boolean);
  const atLocs = useCreateAtLocations(isEditMode ? editOwner : isStudioRole ? undefined : formData.studio, {
    enabled: open && (isEditMode ? !!editOwner : isStudioRole || superStudioCoupon),
    initial: isEditMode
      ? [editOwner, ...originalExtras]
      : isStudioRole
        ? (selectedLocationId ? [selectedLocationId] : isAllLocations ? [] : [(admin?.studio as any)?._id || ''])
        : [formData.studio],
  });
  const mustPickBranch = isStudioRole && isAllLocations && !isEditMode && !atLocs.multi;
  // The coupon's own location (an edit's, or a super admin's picked studio)
  // always stays ticked.
  const fixedLocation = isEditMode ? editOwner : !isStudioRole ? formData.studio : '';
  const setAtLocsKeepingStudio = (ids: string[]) =>
    atLocs.setSelected(fixedLocation && !ids.includes(fixedLocation) ? [fixedLocation, ...ids] : ids);
  const severalLocations = atLocs.multi && atLocs.selected.length > 1;
  const ONE_LOCATION_TYPES = ['event', 'class', 'pack', 'merchandise'];
  // The location whose events / classes / packs the dropdowns list.
  const formLocation = isEditMode ? '' : atLocs.multi ? atLocs.selected[0] || '' : mustPickBranch ? branchForNew : '';
  const prevFormLocation = useRef(formLocation);
  useEffect(() => {
    if (prevFormLocation.current === formLocation) return;
    prevFormLocation.current = formLocation;
    if (isEditMode || !atLocs.multi) return;
    setFormData((prev) => ({ ...prev, event: '', class: '', packs: [], merchandise: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formLocation]);
  const [events, setEvents] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [packs, setPacks] = useState<any[]>([]);
  const [merchandise, setMerchandise] = useState<any[]>([]);

  // Class types this studio actually runs — derived from its own classes, not
  // the global list. Used by the CLASS_TYPE coupon picker.
  const studioClassTypes = React.useMemo(() => {
    const byId = new Map<string, { _id: string; title: string }>();
    for (const cls of classes) {
      const ct = cls?.classType;
      const id = typeof ct === 'object' ? ct?._id : ct;
      if (!id) continue;
      if (!byId.has(id)) {
        byId.set(id, { _id: id, title: typeof ct === 'object' ? ct?.title || 'Class type' : 'Class type' });
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [classes]);

  // Global packs (studio == null) are SweatSocial's inventory. A studio must
  // never be able to discount them, so they never reach the picker — the
  // backend rejects them too, this just avoids offering an option that fails.
  const selectablePacks = React.useMemo(
    () => (isStudioRole ? packs.filter((p: any) => !!p.studio) : packs),
    [packs, isStudioRole],
  );

  const filterUpcoming = useCallback((items: any[], dateField: string = 'date', selectedId?: string) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of day for comparison
    
    return items.filter((item) => {
      // Always include the selected item (for editing past events/classes)
      if (selectedId && item._id === selectedId) return true;
      
      if (!item[dateField]) return false;
      const itemDate = new Date(item[dateField]);
      itemDate.setHours(0, 0, 0, 0);
      return itemDate >= now;
    }).sort((a, b) => {
      // Sort by date ascending
      const dateA = new Date(a[dateField]).getTime();
      const dateB = new Date(b[dateField]).getTime();
      return dateA - dateB;
    });
  }, []);

  // Under "All locations" the event / class / pack / merchandise options are
  // the CHOSEN location's (a coupon lives at one place), pinned to it; nothing
  // loads until one is chosen. Only the latest load may write the lists.
  const dropdownReqRef = useRef(0);
  const fetchDropdownData = useCallback(async () => {
    const req = ++dropdownReqRef.current;
    if ((mustPickBranch && !branchForNew) || (atLocs.multi && isStudioRole && !formLocation)) {
      setEvents([]);
      setClasses([]);
      setPacks([]);
      setMerchandise([]);
      return;
    }
    const pin = isStudioRole && formLocation ? formLocation : undefined;
    try {
      const requests: Promise<any>[] = [
        isStudioRole ? Promise.resolve(null) : apiService.getStudios(),
        apiService.getEvents({}, pin),
        apiService.getAllClasses({}, pin),
        apiService.getPacks({}, pin),
        // Studio roles are pinned server-side; a super-admin gets everything and
        // the option label carries the studio name to disambiguate.
        apiService.getMerchandise({ pageSize: 100 }, pin),
      ];
      const [studiosRes, eventsRes, classesRes, packsRes, merchRes] = await Promise.all(requests);
      if (req !== dropdownReqRef.current) return;

      setStudios(studiosRes?.data || studiosRes?.studios || []);
      
      // Filter events to only show upcoming ones (or selected event if editing)
      const allEvents = eventsRes?.events || eventsRes?.data || [];
      const selectedEventId = coupon?.event?._id || coupon?.event || '';
      const upcomingEvents = filterUpcoming(allEvents, 'date', selectedEventId);
      setEvents(upcomingEvents);
      
      // Classes don't have dates (they're templates), so show all
      setClasses(classesRes?.classes || classesRes?.data || []);
      // Studio-role pack list comes back as { studioPacks: [...] }, while the
      // super-admin endpoint returns { packs: [...] } / { data: [...] }.
      setPacks(packsRes?.packs || packsRes?.studioPacks || packsRes?.data || []);
      setMerchandise(merchRes?.merchandise || merchRes?.data || []);
    } catch (err) {
      console.error('Error fetching dropdown data:', err);
    }
  }, [filterUpcoming, coupon, isStudioRole, mustPickBranch, branchForNew, atLocs.multi, formLocation]);

  useEffect(() => {
    if (open) {
      fetchDropdownData();
    }
  }, [open, fetchDropdownData]);

  useEffect(() => {
    if (open) {
      if (coupon) {
        // Extract IDs from nested objects or use direct values
        const studioId = typeof coupon.studio === 'object' && coupon.studio?._id 
          ? coupon.studio._id 
          : coupon.studio || '';
        const eventId = typeof coupon.event === 'object' && coupon.event?._id 
          ? coupon.event._id 
          : coupon.event || '';
        const classId = typeof coupon.class === 'object' && coupon.class?._id
          ? coupon.class._id
          : coupon.class || '';
        const classTypeId = typeof coupon.classType === 'object' && coupon.classType?._id
          ? coupon.classType._id
          : coupon.classType || '';
        // Prefer the multi-pack list; fall back to the legacy single `pack` so
        // coupons created before multi-select still open with their selection.
        const idOf = (p: any) => (typeof p === 'object' && p?._id ? p._id : p);
        const selectedPackIds: string[] = Array.isArray(coupon.packs) && coupon.packs.length
          ? coupon.packs.map(idOf).filter(Boolean)
          : (coupon.pack ? [idOf(coupon.pack)].filter(Boolean) : []);
        const merchandiseId = typeof coupon.merchandise === 'object' && coupon.merchandise?._id
          ? coupon.merchandise._id
          : coupon.merchandise || '';

        setFormData({
          code: coupon.code || '',
          title: coupon.title || '',
          description: coupon.description || '',
          discountType: coupon.discountType || 'amount',
          discountValue: coupon.discountValue?.toString() || '',
          maxDiscountAmount: coupon.maxDiscountAmount?.toString() || '',
          applicableType: coupon.applicableType || 'all',
          merchandise: merchandiseId,
          studio: studioId,
          event: eventId,
          class: classId,
          classType: classTypeId,
          packs: selectedPackIds,
          minOrderAmount: coupon.minOrderAmount?.toString() || '',
          maxUsage: coupon.maxUsage?.toString() || '',
          isActive: coupon.isActive !== undefined ? coupon.isActive : true,
          showOnCheckout: coupon.showOnCheckout !== undefined ? coupon.showOnCheckout : false,
          validFrom: coupon.validFrom
            ? new Date(coupon.validFrom).toISOString().split('T')[0]
            : '',
          validUntil: '',
          // Older coupons may carry either date; show whichever ends first.
          expiresAt: (() => {
            const dates = [coupon.validUntil, coupon.expiresAt].filter((d): d is string => !!d).map((d) => new Date(d).getTime());
            return dates.length ? new Date(Math.min(...dates)).toISOString().split('T')[0] : '';
          })(),
        });
      } else {
        resetForm();
      }
    }
  }, [open, coupon]);

  const resetForm = () => {
    setFormData({
      code: '',
      title: '',
      description: '',
      discountType: 'amount',
      discountValue: '',
      maxDiscountAmount: '',
      applicableType: 'all',
      merchandise: '',
      studio: '',
      event: '',
      class: '',
      classType: '',
      // Multi-select: one coupon can cover any number of packs. The backend
      // still accepts the legacy single `pack`, but we always send `packs`.
      packs: [] as string[],
      minOrderAmount: '',
      maxUsage: '',
      isActive: true,
      showOnCheckout: false,
      validFrom: '',
      validUntil: '',
      expiresAt: '',
    });
    setError(null);
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      
      // When applicable type changes, clear related fields
      if (field === 'applicableType') {
        if (value !== 'class_type') newData.classType = '';
        if (value === 'all' || value === 'all_events' || value === 'all_classes' || value === 'all_packs' || value === 'global_packs' || value === 'all_merchandise') {
          newData.studio = '';
          newData.event = '';
          newData.class = '';
          newData.packs = [];
          newData.merchandise = '';
        } else if (value === 'studio' || value === 'studio_packs') {
          newData.event = '';
          newData.class = '';
          newData.packs = [];
        } else if (value === 'event') {
          newData.studio = '';
          newData.class = '';
          newData.packs = [];
        } else if (value === 'class') {
          newData.studio = '';
          newData.event = '';
          newData.packs = [];
        } else if (value === 'class_type') {
          newData.studio = '';
          newData.event = '';
          newData.class = '';
          newData.packs = [];
          newData.merchandise = '';
        } else if (value === 'pack') {
          newData.studio = '';
          newData.event = '';
          newData.class = '';
        }
      }
      
      return newData;
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.code.trim()) {
        throw new Error('Code is required');
      }

      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }

      if (!formData.discountValue || parseFloat(formData.discountValue) < 0) {
        throw new Error('Discount value must be a positive number');
      }

      if (formData.discountType === 'percentage' && parseFloat(formData.discountValue) > 100) {
        throw new Error('Percentage discount cannot exceed 100%');
      }

      if (mustPickBranch && !branchForNew) {
        throw new Error('Choose which location this coupon belongs to');
      }
      if (atLocs.multi && !atLocs.selected.length) {
        throw new Error('Choose at least one location for this coupon');
      }
      if (severalLocations && ONE_LOCATION_TYPES.includes(formData.applicableType)) {
        throw new Error(
          'A coupon for one event, class, pack or product belongs to one location. Tick one location, or choose a type like "All my classes".',
        );
      }

      // Validate applicable type
      if (!isStudioRole && formData.applicableType === 'studio' && !formData.studio) {
        throw new Error('Please select a studio');
      }
      if (formData.applicableType === 'event' && !formData.event) {
        throw new Error('Please select an event');
      }
      if (formData.applicableType === 'class' && !formData.class) {
        throw new Error('Please select a class');
      }
      if (formData.applicableType === 'class_type' && !formData.classType) {
        throw new Error('Please select a class type');
      }
      if (formData.applicableType === 'merchandise' && !formData.merchandise) {
        setError('Select the product this coupon applies to');
        return;
      }
      if (formData.applicableType === 'pack' && formData.packs.length === 0) {
        throw new Error('Select at least one pack this coupon applies to');
      }
      if (!isStudioRole && formData.applicableType === 'studio_packs' && !formData.studio) {
        throw new Error('Please select a studio for studio packs');
      }
      // all_events, all_classes, all_packs, global_packs don't need specific entity selection

      const payload: any = {
        code: formData.code.trim().toUpperCase(),
        title: formData.title.trim(),
        description: formData.description.trim(),
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        applicableType: formData.applicableType,
        isActive: formData.isActive,
        showOnCheckout: formData.showOnCheckout,
      };

      // Add applicable entity
      if (formData.applicableType === 'studio' && formData.studio) {
        payload.studio = formData.studio;
      } else if (formData.applicableType === 'event' && formData.event) {
        payload.event = formData.event;
      } else if (formData.applicableType === 'class' && formData.class) {
        payload.class = formData.class;
      } else if (formData.applicableType === 'class_type' && formData.classType) {
        payload.classType = formData.classType;
      } else if (formData.applicableType === 'pack' && formData.packs.length) {
        payload.packs = formData.packs;
      } else if (formData.applicableType === 'studio_packs' && formData.studio) {
        payload.studio = formData.studio;
      } else if (formData.applicableType === 'merchandise' && formData.merchandise) {
        payload.merchandise = formData.merchandise;
      }

      // Optional fields
      if (formData.description) {
        payload.description = formData.description.trim();
      }
      if (formData.maxDiscountAmount) {
        payload.maxDiscountAmount = parseFloat(formData.maxDiscountAmount);
      }
      if (formData.minOrderAmount) {
        payload.minOrderAmount = parseFloat(formData.minOrderAmount);
      }
      if (formData.maxUsage && formData.maxUsage.trim() !== '' && parseInt(formData.maxUsage) > 0) {
        payload.maxUsage = parseInt(formData.maxUsage);
      }
      if (formData.validFrom) {
        payload.validFrom = new Date(formData.validFrom).toISOString();
      }
      if (formData.expiresAt) {
        // One expiry date in the UI; mirrored into both backend fields.
        payload.expiresAt = new Date(formData.expiresAt).toISOString();
        payload.validUntil = payload.expiresAt;
      }

      if (!isEditMode && atLocs.multi) {
        // ONE coupon: it belongs to the first ticked location (a super admin's
        // picked studio) and also works at the rest.
        const owner = isStudioRole ? atLocs.selected[0] : formData.studio;
        const extras = atLocs.selected.filter((id) => id !== owner);
        if (extras.length) payload.applicableStudios = extras;
        await apiService.createCoupon(payload, isStudioRole ? owner : undefined);
      } else if (isEditMode) {
        payload.couponId = coupon._id;
        const originalOwner = studioIdOf(coupon.createdByStudio) || studioIdOf(coupon.studio);
        if (originalExtras.length && editOwner !== originalOwner) {
          // Moved to another studio: the old studio's extra locations go.
          payload.applicableStudios = atLocs.multi ? atLocs.selected.filter((id) => id !== editOwner) : [];
        } else if (atLocs.multi) {
          // Locations this account cannot manage are not in the list, so they
          // are kept as they were rather than silently removed.
          const shown = new Set(atLocs.creatable.map((l) => l._id));
          const next = [
            ...atLocs.selected.filter((id) => id !== editOwner),
            ...originalExtras.filter((id) => !shown.has(id)),
          ];
          const same = next.length === originalExtras.length && next.every((id) => originalExtras.includes(id));
          if (!same) payload.applicableStudios = ONE_LOCATION_TYPES.includes(formData.applicableType) ? [] : next;
        }
        await apiService.updateCoupon(
          payload,
          studioIdOf(coupon.createdByStudio) || studioIdOf(coupon.studio),
        );
      } else {
        await apiService.createCoupon(payload, mustPickBranch ? branchForNew : undefined);
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {isEditMode ? 'Edit Coupon' : 'Create Coupon'}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {atLocs.multi && (
            <LocationChecks
              locations={atLocs.creatable}
              selected={atLocs.selected}
              onChange={setAtLocsKeepingStudio}
              label="Works at"
              severalHint="One coupon, one code — members can use it at every ticked location."
            />
          )}
          {mustPickBranch && (
            <BranchSelect
              value={branchForNew}
              onChange={(v) => {
                setBranchForNew(v);
                // Items picked from the previous location's lists are not
                // at this one (the lists reload via fetchDropdownData).
                setFormData((prev) => ({
                  ...prev,
                  event: '',
                  class: '',
                  packs: [],
                  merchandise: '',
                }));
              }}
              minRole="manager"
            />
          )}
          <TextField
            label="Coupon Code *"
            value={formData.code}
            onChange={(e) => {
              // Remove spaces from input
              const valueWithoutSpaces = e.target.value.replace(/\s/g, '');
              handleChange('code', valueWithoutSpaces.toUpperCase());
            }}
            fullWidth
            disabled={isEditMode}
            helperText="Coupon code will be converted to uppercase (no spaces allowed)"
            error={formData.code.includes(' ')}
          />

          <TextField
            label="Title *"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            fullWidth
            placeholder="e.g., Summer Sale, New User Discount"
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Optional description for the coupon"
          />

          <FormControl fullWidth>
            <InputLabel>Discount Type *</InputLabel>
            <Select
              value={formData.discountType}
              label="Discount Type *"
              onChange={(e) => handleChange('discountType', e.target.value)}
            >
              <MenuItem value="amount">Fixed Amount (₹)</MenuItem>
              <MenuItem value="percentage">Percentage (%)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={`Discount Value * (${formData.discountType === 'percentage' ? 'Percentage' : 'Amount in ₹'})`}
            type="number"
            value={formData.discountValue}
            onChange={(e) => handleChange('discountValue', e.target.value)}
            onWheel={stopWheelNudge}
            fullWidth
            inputProps={{
              min: 0,
              max: formData.discountType === 'percentage' ? 100 : undefined,
              step: formData.discountType === 'percentage' ? 1 : 0.01,
            }}
            helperText={
              formData.discountType === 'percentage'
                ? 'Enter discount percentage (0-100)'
                : 'Enter discount amount in rupees'
            }
          />

          {formData.discountType === 'percentage' && (
            <TextField
              label="Maximum Discount Cap (₹)"
              type="number"
              value={formData.maxDiscountAmount}
              onChange={(e) => handleChange('maxDiscountAmount', e.target.value)}
              onWheel={stopWheelNudge}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Optional: Maximum discount amount in rupees"
            />
          )}

          <FormControl fullWidth>
            <InputLabel>Applicable To *</InputLabel>
            <Select
              value={formData.applicableType}
              label="Applicable To *"
              onChange={(e) => handleChange('applicableType', e.target.value)}
              disabled={
                formData.applicableType === 'global_packs' && admin?.role !== 'super_admin'
              }
            >
              {/* Labels are written from the operator's point of view. A studio
                  can only ever discount its own inventory, so "my" is accurate
                  for them; super admins get the platform-wide wording and the
                  extra global options. */}
              <ListSubheader>Everything</ListSubheader>
              <MenuItem value="all" sx={{ fontWeight: 600 }}>
                {isStudioRole ? 'Everything I sell (classes, events & packs)' : 'All bookings & pack purchases'}
              </MenuItem>

              <ListSubheader>Classes</ListSubheader>
              <MenuItem value="all_classes">{isStudioRole ? 'All my classes' : 'All class bookings'}</MenuItem>
              {(isMatchdayStudio || formData.applicableType === 'class_type') && (
                <MenuItem value="class_type" disabled={!isMatchdayStudio}>
                  A whole class type
                </MenuItem>
              )}
              <MenuItem value="class">One specific class</MenuItem>

              <ListSubheader>Events</ListSubheader>
              <MenuItem value="all_events">{isStudioRole ? 'All my events' : 'All event bookings'}</MenuItem>
              <MenuItem value="event">One specific event</MenuItem>

              {/* Studio-wide bookings: meaningless to a studio (it IS the
                  studio) — "Everything I sell" already covers that case. */}
              {!isStudioRole && (
                <ListSubheader>Studio</ListSubheader>
              )}
              {!isStudioRole && (
                <MenuItem value="studio">All bookings at one studio (classes &amp; events)</MenuItem>
              )}

              <ListSubheader>Packs</ListSubheader>
              {isStudioRole ? (
                <MenuItem value="studio_packs">All of my packs</MenuItem>
              ) : (
                <MenuItem value="all_packs">All pack purchases (SweatSocial + studio)</MenuItem>
              )}
              {/* A studio-owned coupon created before these were restricted
                  would otherwise render the Select blank. Keep the current
                  value visible (disabled) so the operator can see what it is
                  and pick a valid replacement — the backend rejects saving it. */}
              {isStudioRole && (formData.applicableType === 'all_packs' || formData.applicableType === 'global_packs') && (
                <MenuItem value={formData.applicableType} disabled>
                  {formData.applicableType === 'all_packs'
                    ? 'All pack purchases (no longer available — pick another)'
                    : 'SweatSocial packs (no longer available — pick another)'}
                </MenuItem>
              )}
              {!isStudioRole && (admin?.role === 'super_admin' || formData.applicableType === 'global_packs') && (
                <MenuItem value="global_packs">SweatSocial packs only</MenuItem>
              )}
              {!isStudioRole && <MenuItem value="studio_packs">All packs from one studio</MenuItem>}
              <MenuItem value="pack">Selected packs only…</MenuItem>

              <ListSubheader>Shop</ListSubheader>
              <MenuItem value="all_merchandise">{isStudioRole ? 'All of my products' : "All merchandise (studio's shop)"}</MenuItem>
              <MenuItem value="merchandise">One specific product</MenuItem>
            </Select>
          </FormControl>

          {!isStudioRole && (formData.applicableType === 'studio' || formData.applicableType === 'studio_packs') && (
            <FormControl fullWidth>
              <InputLabel>Select Studio *</InputLabel>
              <Select
                value={formData.studio}
                label="Select Studio *"
                onChange={(e) => handleChange('studio', e.target.value)}
              >
                {studios.map((studio) => (
                  <MenuItem key={studio._id} value={studio._id}>
                    {studio.studioName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {formData.applicableType === 'event' && (
            <FormControl fullWidth>
              <InputLabel>Select Event *</InputLabel>
              <Select
                value={formData.event}
                label="Select Event *"
                onChange={(e) => handleChange('event', e.target.value)}
              >
                {events.map((event) => (
                  <MenuItem key={event._id} value={event._id}>
                    {event.eventName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {formData.applicableType === 'class' && (
            <FormControl fullWidth>
              <InputLabel>Select Class *</InputLabel>
              <Select
                value={formData.class}
                label="Select Class *"
                onChange={(e) => handleChange('class', e.target.value)}
              >
                {classes.map((cls) => (
                  <MenuItem key={cls._id} value={cls._id}>
                    {cls.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {formData.applicableType === 'class_type' && (
            <FormControl fullWidth>
              <InputLabel>Class type this coupon applies to *</InputLabel>
              <Select
                value={formData.classType}
                label="Class type this coupon applies to *"
                onChange={(e) => handleChange('classType', e.target.value)}
              >
                {studioClassTypes.length === 0 && (
                  <MenuItem value="" disabled>
                    No class types found for this studio
                  </MenuItem>
                )}
                {studioClassTypes.map((ct) => (
                  <MenuItem key={ct._id} value={ct._id}>
                    {ct.title}
                  </MenuItem>
                ))}
                {/* Keep an unknown saved value visible when editing. */}
                {formData.classType &&
                  !studioClassTypes.some((ct) => ct._id === formData.classType) && (
                    <MenuItem value={formData.classType}>
                      {typeof coupon?.classType === 'object' && coupon?.classType?.title
                        ? coupon.classType.title
                        : 'Current class type'}
                    </MenuItem>
                  )}
              </Select>
            </FormControl>
          )}

          {formData.applicableType === 'pack' && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={selectablePacks}
              value={selectablePacks.filter((p) => formData.packs.includes(p._id))}
              onChange={(_, selected) =>
                handleChange('packs', selected.map((p: any) => p._id))
              }
              getOptionLabel={(p: any) => p.title || 'Untitled pack'}
              isOptionEqualToValue={(a: any, b: any) => a._id === b._id}
              // Studio owners only ever see their own packs, so the studio name
              // would be noise; super admins need it to tell duplicates apart.
              groupBy={isStudioRole ? undefined : (p: any) => p.studio?.studioName || 'SweatSocial'}
              renderOption={(props, option: any, { selected }) => (
                <li {...props} key={option._id}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body2">{option.title}</Typography>
                    {option.totalPrice != null && (
                      <Typography variant="caption" color="text.secondary">
                        ₹{option.totalPrice}
                        {option.totalClasses ? ` · ${option.totalClasses} classes` : ' · unlimited'}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option: any, index) => (
                  <Chip
                    size="small"
                    label={option.title}
                    {...getTagProps({ index })}
                    key={option._id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Packs this coupon applies to *"
                  placeholder={formData.packs.length ? '' : 'Search and select packs…'}
                  helperText={
                    selectablePacks.length === 0
                      ? 'No packs available yet — create a pack first.'
                      : formData.packs.length === 0
                        ? `Pick one or more of your ${selectablePacks.length} packs`
                        : `${formData.packs.length} of ${selectablePacks.length} packs selected`
                  }
                />
              )}
            />
          )}

          {formData.applicableType === 'pack' && selectablePacks.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, mt: -1 }}>
              <Button
                size="small"
                onClick={() => handleChange('packs', selectablePacks.map((p) => p._id))}
                disabled={formData.packs.length === selectablePacks.length}
              >
                Select all
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => handleChange('packs', [])}
                disabled={formData.packs.length === 0}
              >
                Clear
              </Button>
            </Box>
          )}

          {formData.applicableType === 'merchandise' && (
            <FormControl fullWidth>
              <InputLabel>Select Product *</InputLabel>
              <Select
                value={formData.merchandise}
                label="Select Product *"
                onChange={(e) => handleChange('merchandise', e.target.value)}
              >
                {merchandise.length === 0 && (
                  <MenuItem value="" disabled>
                    {isStudioRole ? 'No products yet' : 'Pick a studio first, or none have products'}
                  </MenuItem>
                )}
                {merchandise.map((m: any) => (
                  <MenuItem key={m._id} value={m._id}>
                    {m.title} — ₹{m.price}
                    {m.studio?.studioName ? ` (${m.studio.studioName})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Minimum Order Amount (₹)"
            type="number"
            value={formData.minOrderAmount}
            onChange={(e) => handleChange('minOrderAmount', e.target.value)}
            onWheel={stopWheelNudge}
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            helperText="Optional: Minimum order amount required to use this coupon"
          />

          <TextField
            label="Maximum Usage"
            type="number"
            value={formData.maxUsage}
            onChange={(e) => handleChange('maxUsage', e.target.value)}
            onWheel={stopWheelNudge}
            fullWidth
            inputProps={{ min: 1 }}
            helperText="Optional: Leave empty for unlimited usage"
          />

          <TextField
            label="Valid From"
            type="date"
            value={formData.validFrom}
            onChange={(e) => handleChange('validFrom', e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Optional: Coupon becomes valid from this date"
          />

          <TextField
            label="Expiry Date"
            type="date"
            value={formData.expiresAt}
            onChange={(e) => handleChange('expiresAt', e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Optional: Coupon expires on this date"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
              />
            }
            label="Active"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.showOnCheckout}
                onChange={(e) => handleChange('showOnCheckout', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Show on Checkout Page
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  If enabled, coupon will appear in checkout page list for users to select. If disabled, coupon can only be used by entering the code manually.
                </Typography>
              </Box>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !formData.code ||
            !formData.title ||
            !formData.discountValue ||
            (!isStudioRole && formData.applicableType === 'studio' && !formData.studio) ||
            (formData.applicableType === 'event' && !formData.event) ||
            (formData.applicableType === 'class' && !formData.class) ||
            (formData.applicableType === 'class_type' && !formData.classType) ||
            (formData.applicableType === 'pack' && formData.packs.length === 0) ||
            (!isStudioRole && formData.applicableType === 'studio_packs' && !formData.studio)
          }
          startIcon={loading && <CircularProgress size={16} />}
        >
          {isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CouponFormDialog;

